import * as React from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Filter } from '../../interfaces';
import MultiSelectFilter from '../filtering/MultiSelectFilter';
import SingleSelectFilter from '../filtering/SingleSelectFilter';

export type PopoverFilter =
  | (Filter & { label: string; inputType?: 'multi' })
  | {
      label: string;
      inputType: 'single';
      name: string;
      options: { label: string; value: string }[];
      state: string;
      setState: (value: string) => void;
      defaultValue?: string;
      isToggle?: boolean;
    };

type SingleFilter = Extract<PopoverFilter, { inputType: 'single' }>;

interface Props {
  filters: PopoverFilter[];
  redesignActive?: boolean;
}

function getActiveFilterCount(filters: PopoverFilter[]): number {
  return filters.reduce((count, filter) => {
    if (filter.inputType === 'single') {
      const isActive =
        filter.defaultValue !== undefined
          ? filter.state !== filter.defaultValue
          : !!filter.state;
      return count + (isActive ? 1 : 0);
    }
    const hasActiveFilter =
      filter.options.length > 0 &&
      filter.state.length > 0 &&
      filter.state.length < filter.options.length;
    return count + (hasActiveFilter ? 1 : 0);
  }, 0);
}

function commitSingle(filter: SingleFilter, newValue: string) {
  filter.setState(newValue);
  if (newValue) sessionStorage.setItem(filter.name, JSON.stringify(newValue));
  else sessionStorage.removeItem(filter.name);
}

function commitMulti(
  filter: Extract<PopoverFilter, { inputType?: 'multi' }>,
  newValue: string[],
) {
  filter.setState(newValue);
  sessionStorage.setItem(filter.name, JSON.stringify(newValue));
}

export default function CalendarFilterPopover({
  filters,
  redesignActive,
}: Props) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const [menuState, setMenuState] = React.useState<{
    anchorEl: HTMLElement;
    filter: SingleFilter;
  } | null>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mirror the persistence the filter inputs write on change
  const persistReset = (name: string, value: string | string[]) => {
    if (typeof value === 'string' && !value) {
      sessionStorage.removeItem(name);
      return;
    }
    sessionStorage.setItem(name, JSON.stringify(value));
  };

  const handleClearAll = () => {
    filters.forEach(filter => {
      if (filter.inputType === 'single') {
        const resetValue = filter.defaultValue ?? '';
        filter.setState(resetValue);
        persistReset(filter.name, resetValue);
      } else {
        const allValues = filter.options.map(o => o.value);
        filter.setState(allValues);
        persistReset(filter.name, allValues);
      }
    });
  };

  const activeCount = getActiveFilterCount(filters);
  const hasActiveFilters = activeCount > 0;

  const filterContent = (
    <Stack spacing={1} sx={{ p: 2, width: isDesktop ? 300 : 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 0.5,
        }}>
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={handleClearAll}
            sx={{ textTransform: 'none', fontSize: 'caption.fontSize' }}>
            Reset all
          </Button>
        )}
      </Box>
      {filters.map(filter => (
        <Box key={filter.name}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {filter.label}
          </Typography>
          {filter.inputType === 'single' ? (
            <SingleSelectFilter
              name={filter.name}
              options={filter.options}
              state={filter.state}
              setState={value =>
                filter.setState(
                  typeof value === 'function' ? value(filter.state) : value,
                )
              }
              styleOverride={{ width: '100%', margin: 0 }}
            />
          ) : (
            <MultiSelectFilter
              name={filter.name}
              options={filter.options}
              state={filter.state}
              setState={filter.setState}
              emptyLabel={filter.emptyLabel}
              allLabel={filter.allLabel}
              sx={{ width: '100%', m: 0 }}
            />
          )}
        </Box>
      ))}
    </Stack>
  );

  const redesignedContent = (
    <Box
      sx={{
        width: isDesktop ? 320 : '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {filters.map(filter => {
        if (filter.inputType === 'single' && filter.isToggle) {
          const onOption =
            filter.options.find(o => o.value === 'true') ?? filter.options[0];
          const offOption =
            filter.options.find(o => o.value !== onOption.value) ??
            filter.options[0];
          const checked = filter.state === onOption.value;
          return (
            <Box
              key={filter.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}>
              <Typography
                variant="h5"
                sx={{
                  color: 'text.primary',
                }}>
                {onOption.label}
              </Typography>
              <Switch
                checked={checked}
                onChange={(_, next) =>
                  commitSingle(filter, next ? onOption.value : offOption.value)
                }
              />
            </Box>
          );
        }

        if (filter.inputType === 'single') {
          const currentLabel =
            filter.options.find(o => o.value === filter.state)?.label ??
            filter.options.find(o => o.value === filter.defaultValue)?.label ??
            'All';
          return (
            <Box
              key={filter.name}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'text.secondary',
                }}>
                {filter.label}
              </Typography>
              <Button
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setMenuState({ anchorEl: event.currentTarget, filter })
                }
                fullWidth
                endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
                sx={{
                  justifyContent: 'space-between',
                  height: 40,
                  border: '1px solid #000',
                  borderRadius: '5px',
                  color: 'text.primary',
                  textTransform: 'none',
                  px: 1.75,
                  typography: 'h5',
                }}>
                {currentLabel}
              </Button>
            </Box>
          );
        }

        return (
          <Box
            key={filter.name}
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
              }}>
              {filter.label}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
              {filter.options.map(option => {
                const selected = filter.state.includes(option.value);
                return (
                  <Chip
                    key={option.value}
                    label={option.label}
                    onClick={() =>
                      commitMulti(
                        filter,
                        selected
                          ? filter.state.filter(v => v !== option.value)
                          : [...filter.state, option.value],
                      )
                    }
                    sx={{
                      height: 30,
                      borderRadius: '50px',
                      bgcolor: selected ? '#000' : 'transparent',
                      color: selected ? '#fff' : 'text.secondary',
                      border: selected ? 'none' : '1px solid',
                      borderColor: 'lightOutlineGrey.main',
                      typography: 'caption',
                      fontWeight: 600,
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: 'lightGrey.main',
        }}>
        {hasActiveFilters ? (
          <Button
            onClick={handleClearAll}
            size="small"
            sx={{
              textTransform: 'none',
              color: 'primary.main',
              typography: 'caption',
              fontWeight: 600,
            }}>
            Reset filters
          </Button>
        ) : (
          <Box />
        )}
        <Button
          onClick={handleClose}
          variant="contained"
          size="small"
          sx={{
            bgcolor: '#000',
            color: 'common.white',
            borderRadius: '50px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5,
            '&:hover': { bgcolor: '#000' },
          }}>
          Apply
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {redesignActive ? (
        <Button
          variant="outlined"
          onClick={handleOpen}
          sx={{
            height: 40,
            borderRadius: '5px',
            borderColor: '#000',
            color: 'text.primary',
            textTransform: 'none',
            px: 1.75,
            gap: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            typography: 'h5',
          }}>
          <TuneIcon sx={{ fontSize: 18 }} />
          Filters
          {hasActiveFilters && (
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 18,
                height: 18,
                borderRadius: '50px',
                bgcolor: 'primary.main',
                color: 'common.white',
                px: 0.5,
                // eslint-disable-next-line no-restricted-syntax -- typography-exception: badge glyph below the 11px scale floor
                fontSize: '0.625rem',
                fontWeight: 700,
              }}>
              {activeCount}
            </Box>
          )}
        </Button>
      ) : (
        <Badge
          badgeContent={activeCount}
          color="primary"
          invisible={!hasActiveFilters}
          sx={{
            '& .MuiBadge-badge': {
              top: 6,
              right: 6,
            },
          }}>
          <Button
            variant="outlined"
            onClick={handleOpen}
            sx={{
              height: 44,
              borderRadius: '10px',
              borderColor: '#939393',
              color: 'var(--darkGrey)',
              textTransform: 'none',
              px: 2,
              minWidth: 'auto',
              typography: 'subtitle2',
            }}>
            <TuneIcon sx={{ fontSize: 20, mr: 0.5 }} />
            Filters
          </Button>
        </Badge>
      )}

      {isDesktop ? (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: redesignActive
                ? { mt: 1, borderRadius: '10px', overflow: 'hidden' }
                : { mt: 1, borderRadius: '10px' },
            },
          }}>
          {redesignActive ? redesignedContent : filterContent}
        </Popover>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '16px',
              borderTopRightRadius: '16px',
              maxHeight: '70vh',
            },
          }}>
          {redesignActive ? (
            redesignedContent
          ) : (
            <>
              {filterContent}
              <Box sx={{ p: 2, pt: 0 }}>
                <Button
                  variant="base"
                  fullWidth
                  onClick={handleClose}
                  sx={{ textTransform: 'none' }}>
                  Done
                </Button>
              </Box>
            </>
          )}
        </Drawer>
      )}

      <Menu
        anchorEl={menuState?.anchorEl ?? null}
        open={Boolean(menuState)}
        onClose={() => setMenuState(null)}>
        {menuState?.filter.options.map(option => (
          <MenuItem
            key={option.value}
            selected={option.value === menuState.filter.state}
            onClick={() => {
              commitSingle(menuState.filter, option.value);
              setMenuState(null);
            }}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
